import { useState, useEffect, useLayoutEffect, useRef, useContext, Fragment, type ReactNode, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { subscribeKey } from 'valtio/utils'
import { HoloSignContext } from '../../holoSignContext'
import { packetsReplayState } from '../../state/packetsReplayState'
import styles from './TelemetryPanel.module.css'
import PlayerAvatar from './PlayerAvatar'
import CollapseTab from './CollapseTab'

interface NearbyPlayer { name: string; distance: number }

interface TelemetryData {
  time: string
  date: string
  tps: number | null
  ping: number | null
  x: number; y: number; z: number
  yaw: number; pitch: number
  chunkX: number; chunkZ: number
  gameMode: string | null
  biome: string | null
  lightLevel: number | null
  motionXZ: number; motionY: number
  /** "walk" | "run" | "fly" | "fall" | "jump" | "idle" */
  motionState: string
  nearbyPlayers: NearbyPlayer[]
}

const EMPTY: TelemetryData = {
  time: '–:–:–', date: '',
  tps: null, ping: null,
  x: 0, y: 0, z: 0, yaw: 0, pitch: 0, chunkX: 0, chunkZ: 0,
  gameMode: null, biome: null, lightLevel: null,
  motionXZ: 0, motionY: 0, motionState: 'idle', nearbyPlayers: [],
}

const GAME_MODE_LABEL: Record<string, string> = {
  survival: 'Выживание', creative: 'Творчество',
  adventure: 'Приключение', spectator: 'Спектатор',
}

const MOTION_LABEL: Record<string, string> = {
  idle: 'Покой', walk: 'Ходьба', run: 'Бег',
  jump: 'Прыжок', fall: 'Падение', fly: 'Полёт',
}

/* Little people for the game-mode row (Tabler outlines, 1.7 stroke — same
   voice as the transport icons). Survival wears hazard orange, creative a
   gold star, adventure walks, spectator is a ghost; colors live in CSS via
   data-mode. */
const MODE_FIGURE: Record<string, ReactNode> = {
  survival: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="7" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    </svg>
  ),
  creative: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="7" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h1.5" />
      <path d="M17.8 20.8l-2.2 1.2l.4-2.4l-1.7-1.7l2.4-.4l1.1-2.2l1.1 2.2l2.4 .4l-1.7 1.7l.4 2.4z" />
    </svg>
  ),
  adventure: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13" cy="4" r="1" />
      <path d="M7 21l3-4" />
      <path d="M16 21l-2-4l-3-3l1-6" />
      <path d="M6 12l2-3l4-1l3 3l3 1" />
    </svg>
  ),
  spectator: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 11a7 7 0 0 1 14 0v7a1.78 1.78 0 0 1 -3.1 1.4a1.65 1.65 0 0 0 -2.6 0a1.65 1.65 0 0 1 -2.6 0a1.78 1.78 0 0 1 -3.1 -1.4v-7" />
      <path d="M10 10h.01" />
      <path d="M14 10h.01" />
    </svg>
  ),
}

const WeatherSunIcon = () => (
  <svg className={styles.weatherSun} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g className={styles.weatherSunRays} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 1.8v3" />
      <path d="M12 19.2v3" />
      <path d="M1.8 12h3" />
      <path d="M19.2 12h3" />
      <path d="M4.8 4.8l2.1 2.1" />
      <path d="M17.1 17.1l2.1 2.1" />
      <path d="M19.2 4.8l-2.1 2.1" />
      <path d="M6.9 17.1l-2.1 2.1" />
    </g>
    <circle className={styles.weatherSunCore} cx="12" cy="12" r="4.6" fill="currentColor" />
  </svg>
)

/* ─── Edit-mode block IDs (persisted to localStorage) ─── */
type BlockId =
  | 'time' | 'gamemode' | 'coords' | 'camera' | 'tags'
  | 'speed' | 'nearby' | 'packets' | 'memory' | 'fps'

const HIDDEN_KEY = 'replay.telemetry.hidden'

const loadHidden = (): Set<BlockId> => {
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')
    return new Set(Array.isArray(raw) ? raw as BlockId[] : [])
  } catch { return new Set() }
}

const saveHidden = (s: Set<BlockId>) => {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s])) } catch {}
}

function readTelemetry (): TelemetryData {
  const bot = (globalThis as any).bot
  if (!bot) return EMPTY
  try {
    const pos = bot.entity?.position
    const vel = bot.entity?.velocity
    const x = pos ? Math.round(pos.x) : 0
    const y = pos ? Math.round(pos.y) : 0
    const z = pos ? Math.round(pos.z) : 0
    const yaw   = bot.entity?.yaw   != null ? (bot.entity.yaw   * (180 / Math.PI)) : 0
    const pitch = bot.entity?.pitch != null ? (bot.entity.pitch * (180 / Math.PI)) : 0
    const vx = vel?.x ?? 0
    const vz = vel?.z ?? 0
    const vy = vel?.y ?? 0
    const motionXZ = Math.sqrt(vx * vx + vz * vz)
    const motionState = bot.entity?.isInWater ? 'fly'
      : !bot.entity?.onGround && Math.abs(vy) > 0.08 ? (vy > 0 ? 'jump' : 'fall')
      : motionXZ > 0.22 ? 'run'
      : motionXZ > 0.04 ? 'walk'
      : 'idle'

    const players: NearbyPlayer[] = []
    if (bot.entities && pos) {
      for (const [, ent] of Object.entries(bot.entities as Record<string, any>)) {
        if (ent.type !== 'player' || ent.username === bot.username) continue
        const dx = ent.position.x - pos.x
        const dz = ent.position.z - pos.z
        const dy = ent.position.y - pos.y
        const dist = Math.round(Math.sqrt(dx * dx + dy * dy + dz * dz))
        if (dist < 300) players.push({ name: ent.username ?? '?', distance: dist })
      }
      players.sort((a, b) => a.distance - b.distance)
    }

    let lightLevel: number | null = null
    let biome: string | null = null
    try {
      if (bot.world && pos) {
        const block = bot.blockAt?.(pos)
        if (block) {
          lightLevel = (block.light ?? 0)
          const biomeObj = block.biome
          if (biomeObj) {
            const raw = biomeObj.displayName ?? biomeObj.name ?? null
            biome = typeof raw === 'string' ? prettifyBiome(raw) : null
          }
        }
      }
    } catch {}

    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    const date = now.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })

    return {
      time, date,
      tps: null,
      ping: bot._client?.latency ?? null,
      x, y, z,
      yaw: parseFloat(yaw.toFixed(1)),
      pitch: parseFloat(pitch.toFixed(1)),
      chunkX: Math.floor(x / 16),
      chunkZ: Math.floor(z / 16),
      gameMode: bot.game?.gameMode ?? null,
      biome,
      lightLevel,
      motionXZ: parseFloat(motionXZ.toFixed(2)),
      motionY: parseFloat(vy.toFixed(2)),
      motionState,
      nearbyPlayers: players.slice(0, 6),
    }
  } catch {
    return EMPTY
  }
}

function prettifyBiome (raw: string): string {
  const cleaned = raw.replace(/^minecraft:/, '').replace(/_/g, ' ').toLowerCase()
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

const FPS_SAMPLES = 48
const PING_SAMPLES = 32
const MEM_SAMPLES = 48

interface PacketStats { down: number; downKbs: number; up: number; upKbs: number }
const PACKETS_EMPTY: PacketStats = { down: 0, downKbs: 0, up: 0, upKbs: 0 }

export default function TelemetryPanel () {
  const [collapsed, setCollapsed] = useState(packetsReplayState.telemetryCollapsed)
  // Let external toggles (top command bar / "T" key, via packetsReplayState.telemetryCollapsed)
  // drive collapse — the CollapseTab still writes the same flag, so no feedback loop.
  useEffect(() => subscribeKey(packetsReplayState, 'telemetryCollapsed', (v) => setCollapsed(v)), [])
  const [editMode, setEditMode] = useState(false)
  const [hidden, setHidden] = useState<Set<BlockId>>(loadHidden)
  const [data, setData] = useState<TelemetryData>(EMPTY)

  // When embedded in a hanging holo-sign, the panel lives on a CSS3D slab. The
  // memory/fps cards must stay on the slab (rendered inline) instead of being
  // portaled to document.body and positioned at viewport coords.
  const embedded = useContext(HoloSignContext) !== null

  const wrapperRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const metricsRowRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    // Inline (on the slab) the cards flow in document order — no viewport-fixed
    // positioning to compute.
    if (embedded) return
    const metrics = metricsRowRef.current
    const panel = panelRef.current
    if (!metrics || !panel) return

    let frame = 0
    const readScale = () => {
      const rootStyle = getComputedStyle(document.documentElement)
      const uiRaw = rootStyle.getPropertyValue('--ui-scale')
      const guiRaw = rootStyle.getPropertyValue('--guiScale')
      const ui = Number.parseFloat(uiRaw)
      const gui = Number.parseFloat(guiRaw)
      const uiScale = Number.isFinite(ui) && ui > 0 ? ui : 1
      const guiScale = Number.isFinite(gui) && gui > 0 ? gui : 1
      return uiScale * guiScale
    }

    const update = () => {
      frame = 0
      const scale = readScale()
      metrics.style.setProperty('--metrics-scale', String(scale))
      const panelRect = panel.getBoundingClientRect()
      const expandedY = panelRect.bottom + 8 * scale
      const collapsedY = Math.max(14 * scale, panelRect.top + 2 * scale)
      metrics.style.setProperty('--metrics-y-expanded', `${expandedY}px`)
      metrics.style.setProperty('--metrics-y-collapsed', `${collapsedY}px`)
    }

    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    const ro = new ResizeObserver(schedule)
    ro.observe(panel)
    ro.observe(metrics)
    schedule()
    window.addEventListener('resize', schedule)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      ro.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [hidden, embedded])
  const [fps, setFps] = useState(0)
  const [fpsHistory, setFpsHistory] = useState<number[]>([])
  const [pingHistory, setPingHistory] = useState<number[]>([])
  const [memMb, setMemMb] = useState(0)
  const [memHistory, setMemHistory] = useState<number[]>([])
  const [packets, setPackets] = useState<PacketStats>(PACKETS_EMPTY)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  /* Block visibility helpers */
  const isVisible = (id: BlockId) => !hidden.has(id)
  const removeBlock = (id: BlockId) => {
    setHidden(prev => {
      const next = new Set(prev); next.add(id); saveHidden(next); return next
    })
  }
  const resetBlocks = () => {
    setHidden(new Set()); saveHidden(new Set())
  }

  // Telemetry tick (250ms): coords, ping, memory
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const next = readTelemetry()
      setData(next)
      if (next.ping !== null) {
        setPingHistory(h => {
          const n = [...h, next.ping!]
          if (n.length > PING_SAMPLES) n.shift()
          return n
        })
      }
      const mem = (performance as any)?.memory?.usedJSHeapSize ?? 0
      if (mem) {
        const mb = mem / 1024 / 1024
        setMemMb(mb)
        setMemHistory(h => {
          const n = [...h, mb]
          if (n.length > MEM_SAMPLES) n.shift()
          return n
        })
      }
    }, 250)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])

  // FPS counter — show the renderer's REAL rendered fps, which honours the
  // frame-limit setting, instead of counting raw rAF callbacks (the monitor
  // refresh rate, which never changes when the user caps fps). The world
  // renderer tracks this in `lastFps`, refreshed once per second.
  useEffect(() => {
    const id = setInterval(() => {
      const real = (window as any).world?.lastFps
      if (typeof real === 'number' && real > 0) {
        setFps(real)
        setFpsHistory(h => {
          const n = [...h, real]
          if (n.length > FPS_SAMPLES) n.shift()
          return n
        })
      }
    }, 500)
    return () => clearInterval(id)
  }, [])

  // Packet rate counters (1Hz aggregation)
  useEffect(() => {
    const bot = (globalThis as any).bot
    if (!bot?._client) return
    const acc = { downCount: 0, downBytes: 0, upCount: 0, upBytes: 0 }
    const onPacket = (_data: any, _meta: any, buffer: any) => {
      acc.downCount++
      acc.downBytes += buffer?.length ?? 0
    }
    const onWrite = () => { acc.upCount++ }
    bot._client.on('packet', onPacket)
    bot._client.on('writePacket', onWrite)
    const id = setInterval(() => {
      setPackets({
        down: acc.downCount,
        downKbs: acc.downBytes / 1024,
        up: acc.upCount,
        upKbs: 0,
      })
      acc.downCount = 0; acc.downBytes = 0; acc.upCount = 0; acc.upBytes = 0
    }, 1000)
    return () => {
      try { bot._client.off?.('packet', onPacket) } catch {}
      try { bot._client.off?.('writePacket', onWrite) } catch {}
      clearInterval(id)
    }
  }, [])

  const gmLabel = data.gameMode ? (GAME_MODE_LABEL[data.gameMode] ?? data.gameMode) : null
  const motionTag = MOTION_LABEL[data.motionState]
  const hasTags = data.lightLevel !== null
    || (motionTag && data.motionState !== 'idle')
    || !!data.biome

  /* ─── Content blocks (each independently hide-able in edit mode) ─── */
  const contentBlocks: Array<{ id: BlockId; available: boolean; render: () => ReactNode }> = [
    {
      id: 'time', available: true,
      render: () => (
        <div className={styles.timeRow}>
          <div className={styles.timeMain}>
            <span className={styles.timeVal}>{data.time}</span>
            <span className={styles.timeLbl}>{data.date}</span>
          </div>
          {data.tps !== null && (
            <div className={styles.timeStat}>
              <span className={styles.timeStatVal}>{data.tps.toFixed(1)}</span>
              <span className={styles.timeStatLbl}>TPS</span>
            </div>
          )}
          {data.ping !== null && (
            <div className={styles.timeStat}>
              <span className={styles.timeStatVal}>{data.ping}</span>
              <span className={styles.timeStatLbl}>ms</span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'gamemode', available: !!gmLabel,
      render: () => (
        <div className={styles.tagRow}>
          {data.gameMode && MODE_FIGURE[data.gameMode] && (
            <span className={styles.modeFigure} data-mode={data.gameMode} aria-hidden="true">
              {MODE_FIGURE[data.gameMode]}
            </span>
          )}
          <span className={styles.tagPrimary}>{gmLabel}</span>
        </div>
      ),
    },
    {
      id: 'coords', available: true,
      render: () => (
        <div className={styles.section} data-sec="coords">
          <span className={styles.sectionLbl}>Координаты XYZ</span>
          <div className={styles.coords}>
            <span>{data.x}</span>
            <span>{data.y}</span>
            <span>{data.z}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'camera', available: true,
      render: () => (
        <div className={styles.camRow}>
          <div className={styles.camItem}>
            <span className={styles.camLbl}>Yaw</span>
            <span className={styles.camVal}>{data.yaw}°</span>
          </div>
          <div className={styles.camItem}>
            <span className={styles.camLbl}>Pitch</span>
            <span className={styles.camVal}>{data.pitch}°</span>
          </div>
          <div className={styles.camItem}>
            <span className={styles.camLbl}>Чанк X, Z</span>
            <span className={styles.camVal}>{data.chunkX}&nbsp;&nbsp;{data.chunkZ}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'tags', available: !!hasTags,
      render: () => (
        <div className={styles.tagsRow}>
          {data.lightLevel !== null && (
            <span className={`${styles.tag} ${styles.weatherTag}`}>
              <WeatherSunIcon />
              <span>{data.lightLevel}</span>
            </span>
          )}
          {motionTag && data.motionState !== 'idle' && (
            <span className={styles.tag}>{motionTag}</span>
          )}
          {data.biome && <span className={styles.tag}>{data.biome}</span>}
        </div>
      ),
    },
    {
      id: 'speed', available: true,
      render: () => (
        <div className={styles.speedRow}>
          <span className={styles.varSign} aria-hidden="true">v</span>
          <div className={styles.speedItem}>
            <span className={styles.sectionLbl}>Скорость (X/Z)</span>
            <div className={styles.speedVal}>
              {data.motionXZ.toFixed(2)} <span className={styles.dim}>м/с</span>
            </div>
          </div>
          <div className={styles.speedItem}>
            <span className={styles.sectionLbl}>(Y)</span>
            <div className={styles.speedVal}>
              <span className={styles.dim}>{data.motionY >= 0 ? '↑' : '↓'} </span>
              {Math.abs(data.motionY).toFixed(2)} <span className={styles.dim}>м/с</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'nearby', available: data.nearbyPlayers.length > 0,
      render: () => (
        <div className={styles.section}>
          <span className={styles.sectionLbl}>Поблизости</span>
          <div className={styles.nearbyGrid}>
            {data.nearbyPlayers.map((p, i) => (
              <div key={i} className={styles.playerRow}>
                <span className={styles.avatar}><PlayerAvatar name={p.name} size={16} /></span>
                <span className={styles.playerName}>{p.name}</span>
                <span className={styles.playerDist}>{p.distance} м</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'packets', available: true,
      render: () => (
        <div className={styles.section} data-sec="packets">
          <div className={styles.packetText}>
            <span className={styles.sectionLbl}>Пакеты</span>
            <div className={styles.packetsRow}>
              <span className={styles.packetItem}>
                <span className={styles.packetArrow}>↓</span>
                <span className={styles.packetCount}>{packets.down}</span>
                <span className={styles.dim}>({packets.downKbs.toFixed(1)} КБ/с)</span>
              </span>
              <span className={styles.packetItem}>
                <span className={styles.packetArrow}>↑</span>
                <span className={styles.packetCount}>{packets.up}</span>
                <span className={styles.dim}>({packets.upKbs.toFixed(1)} КБ/с)</span>
              </span>
            </div>
          </div>
          {/* An ACTUAL parcel. Network packets, delivered in a cardboard box. */}
          <span className={styles.parcelIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l8 4.5v9l-8 4.5l-8 -4.5v-9z" />
              <path d="M12 12l8 -4.5" />
              <path d="M12 12v9" />
              <path d="M12 12l-8 -4.5" />
              <path d="M16 5.25l-8 4.5" />
            </svg>
          </span>
        </div>
      ),
    },
  ]

  const visibleContent = contentBlocks.filter(b => b.available && isVisible(b.id))

  /* ─── Metric cards (editable too) ─── */
  const memVisible = isVisible('memory')
  const fpsVisible = isVisible('fps')
  const metricsCount = (memVisible ? 1 : 0) + (fpsVisible ? 1 : 0)
  const metricsRow = metricsCount > 0 ? (
    <div
      ref={metricsRowRef}
      className={[
        styles.metricsRow,
        metricsCount === 1 && styles.metricsRowSingle,
        collapsed && styles.metricsRowCollapsed,
      ].filter(Boolean).join(' ')}
      data-replay-ui
      data-metrics-collapsed={collapsed}
    >
      {memVisible && (
        <EditableBlock
          id="memory"
          editing={editMode}
          onRemove={removeBlock}
          index={visibleContent.length}
          kind="card"
        >
          <MetricCard
            label="memory"
            value={memMb < 1024 ? memMb.toFixed(0) : (memMb / 1024).toFixed(1)}
            suffix={memMb < 1024 ? 'MB' : 'GB'}
            color="#9aa6b8"
            history={memHistory}
            maxFloor={Math.max(64, ...memHistory) * 1.05}
            sampleMs={250}
          />
        </EditableBlock>
      )}
      {fpsVisible && (
        <EditableBlock
          id="fps"
          editing={editMode}
          onRemove={removeBlock}
          index={visibleContent.length + 1}
          kind="card"
        >
          <MetricCard
            label="FPS"
            value={String(fps)}
            color={fps >= 50 ? '#7ed4a0' : fps >= 30 ? '#E8B84B' : '#e05555'}
            history={fpsHistory}
            maxFloor={60}
            sampleMs={500}
          />
        </EditableBlock>
      )}
    </div>
  ) : null

  return (
    <>
      <div
        ref={wrapperRef}
        className={[
          styles.wrapper,
          editMode && styles.wrapperEditing,
          collapsed && styles.wrapperCollapsed,
        ].filter(Boolean).join(' ')}
        data-replay-ui
        data-panel-collapsed={collapsed}
      >
        {/* panelWrap keeps CollapseTab as a sibling of the panel so overflow:hidden
         on the panel does not clip the tab that sticks out of the left edge. */}
        <div
          className={[styles.panelWrap, collapsed && styles.panelWrapCollapsed].filter(Boolean).join(' ')}
          data-holo-panel-wrap
        >
          <div
            ref={panelRef}
            className={[
              styles.panel,
              editMode && styles.panelEditing,
              collapsed && styles.panelHidden,
            ].filter(Boolean).join(' ')}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Header — fixed (never editable, always visible) */}
            <div className={styles.header}>
              <div className={styles.title}>
                <span className={styles.titleIcon}>◈</span>
                Телеметрия
              </div>
              <div className={styles.headerBtns}>
                {editMode ? (
                  <>
                    {hidden.size > 0 && (
                      <button
                        className={styles.iconBtn}
                        onClick={resetBlocks}
                        title="Восстановить все блоки"
                      >
                        <span className="pixelart-icons-font-undo" />
                      </button>
                    )}
                    <button
                      className={[styles.iconBtn, styles.iconBtnAccent].join(' ')}
                      onClick={() => setEditMode(false)}
                      title="Готово"
                    >
                      <span className="pixelart-icons-font-check" />
                    </button>
                  </>
                ) : (
                  <button
                    className={styles.iconBtn}
                    onClick={() => setEditMode(true)}
                    title="Редактировать"
                  >
                    <span className="pixelart-icons-font-edit" />
                  </button>
                )}
              </div>
            </div>

            {/* Content blocks */}
            {visibleContent.map((b, i) => (
              <Fragment key={b.id}>
                {i > 0 && !editMode && <div className={styles.divider} />}
                <EditableBlock
                  id={b.id}
                  editing={editMode}
                  onRemove={removeBlock}
                  index={i}
                >
                  {b.render()}
                </EditableBlock>
              </Fragment>
            ))}

          </div>

          {/* Shared collapse tab — left edge (panel is right-anchored). */}
          <CollapseTab
            edge="left"
            collapsed={collapsed}
            onToggle={() => {
              setEditMode(false)
              setCollapsed(v => {
                const next = !v
                packetsReplayState.telemetryCollapsed = next
                return next
              })
            }}
          />
        </div>
      </div>
      {metricsRow && (embedded
        // On the slab: render inline so the cards sit below the panel on the
        // sign face (HoloSignPortal.css flows them in document order).
        ? metricsRow
        // Flat overlay: portal to body and position at the screen corner.
        : (typeof document !== 'undefined' && createPortal(metricsRow, document.body)))}
    </>
  )
}

/* ─────────────────────────────────────────────────────────── */

interface EditableBlockProps {
  id: BlockId
  editing: boolean
  onRemove: (id: BlockId) => void
  index: number
  kind?: 'inline' | 'card'
  children: ReactNode
}

function EditableBlock ({ id, editing, onRemove, index, kind = 'inline', children }: EditableBlockProps) {
  // Pseudo-random per-block delay so the wobble feels organic, not lock-stepped.
  // Entry wave: blocks ripple in with index*0.07s offset.
  // Idle wobble: 13/7-based irregular spread keeps adjacent blocks out of phase.
  const entryDelay = (index * 0.07).toFixed(2)
  const wobbleDelay = (((index * 13) % 7) / 10).toFixed(2)
  const cls = [
    styles.block,
    kind === 'card' && styles.blockCard,
    editing && styles.blockEditing,
  ].filter(Boolean).join(' ')

  return (
    <div
      className={cls}
      style={editing ? {
        animationDelay: `${entryDelay}s, ${wobbleDelay}s`,
      } : undefined}
    >
      {children}
      {editing && (
        <button
          type="button"
          className={styles.removeBtn}
          onClick={(e) => { e.stopPropagation(); onRemove(id) }}
          title="Скрыть блок"
          aria-label={`Скрыть блок ${id}`}
        >
          <span className="pixelart-icons-font-close" />
        </button>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────── */

interface MetricCardProps {
  label: string
  value: string
  color: string
  history: number[]
  maxFloor: number
  suffix?: string
  /** Sampling cadence of `history` — drives the sparkline slide duration. */
  sampleMs: number
}

type SparkSlideStyle = CSSProperties & {
  '--spark-step': string
  '--spark-ms': string
}

function MetricCard ({ label, value, color, history, maxFloor, suffix, sampleMs }: MetricCardProps) {
  const W = 100
  const H = 32
  /* Conveyor scroll: once the buffer is saturated, every new sample shifts
     all points one step left. Instead of letting them jump, the whole line
     group is (re)mounted one step to the RIGHT and slides to 0 over exactly
     one sample interval — points glide through their old positions. During
     the initial fill (length still growing) points don't shift, so no slide. */
  const slideRef = useRef({ arr: [] as number[], len: 0, tick: 0, slide: false })
  const slideState = slideRef.current
  if (slideState.arr !== history) {
    slideState.slide = history.length > 1 && history.length === slideState.len
    slideState.len = history.length
    slideState.arr = history
    slideState.tick++
  }
  const max = Math.max(maxFloor, ...history)
  const step = history.length > 1 ? W / (history.length - 1) : W
  const yFor = (v: number) => {
    const ratio = max > 0 ? Math.min(1, v / max) : 0
    return Math.max(1, Math.min(H - 1, (1 - ratio) * (H - 2) + 1))
  }
  const pts = history.map((v, i) => `${i * step},${yFor(v)}`).join(' ')
  /* One extra lead point at x = -step (clone of the oldest sample) keeps the
     left edge covered while the group is mid-slide. */
  const lead = history.length > 1 ? `${-step},${yFor(history[0])} ` : ''
  const area = history.length > 1 ? `${-step},${H} ${lead}${pts} ${W},${H}` : ''
  const slideStyle: SparkSlideStyle = {
    '--spark-step': `${step}px`,
    '--spark-ms': `${sampleMs}ms`,
  }
  return (
    <div className={styles.metricCard}>
      <span className={styles.metricLbl}>{label}</span>
      <span className={styles.metricVal}>
        <span style={{ color }}>{value}</span>
        {suffix && <span className={styles.metricSuffix}>{suffix}</span>}
      </span>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={styles.metricGraph}>
        {history.length > 1 && (
          <g
            key={slideState.tick}
            className={slideState.slide ? styles.sparkSlide : undefined}
            style={slideState.slide ? slideStyle : undefined}
          >
            <polygon points={area} fill={color} opacity="0.14" />
            <polyline
              points={lead + pts} fill="none" stroke={color}
              strokeWidth="1" strokeLinejoin="round" opacity="0.85"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        )}
      </svg>
    </div>
  )
}
